import Link from "next/link";
import { ArrowRight } from "lucide-react";
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";
import { FaWhatsapp } from "react-icons/fa";
import { buildPoojaHref, useFestivalExperience, useFestivalLocLang } from "./FestivalExperienceContext";
import { LEAD_CTAS, LeadTag, festivalWaLink, trackLeadClick } from "./leadCtas";

const FALLBACK_IMAGE = "/images/Poojas1.jpg";
const formatPrice = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

// One mapped pooja, already checked against the live catalogue by the
// resolver. Price is shown only when it is the visitor's city price.
const FestivalPoojaCard = ({ pooja, slotKey, rank, compact = false }) => {
  const { language, location } = useFestivalLocLang();
  const { experience } = useFestivalExperience();
  const href = buildPoojaHref(pooja, language, location);
  const campaign = experience?.primaryCampaign;

  return (
    <div className="relative h-full">
      <Link
        href={href}
        data-fx-slot={slotKey}
        data-fx-pooja={pooja.slug}
        data-fx-rank={rank}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[hsl(var(--fx-border)/0.35)] bg-[hsl(var(--fx-card)/0.7)] backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-[hsl(var(--fx-border))] hover:shadow-[0_10px_40px_-10px_hsl(var(--fx-accent)/0.5)]"
      >
        {/* Catalogue images are square or portrait; a square frame anchored to the top keeps the deity in view. */}
        <div className={`relative overflow-hidden ${compact ? "h-28" : "aspect-square"}`}>
          <ImageWrapper src={pooja.image || FALLBACK_IMAGE} alt={pooja.name} sizes="(max-width: 768px) 70vw, 280px" className="object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--fx-bg))] via-transparent to-transparent" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-4 pt-2">
          {pooja.label && (
            <span className="flex items-center gap-2 font-sans text-[10.5px] font-semibold uppercase leading-snug tracking-[0.16em] text-[hsl(var(--fx-accent))]">
              <span className="h-px w-4 shrink-0 bg-current opacity-70" aria-hidden="true" />
              {pooja.label}
            </span>
          )}
          <h4 className="font-playfair text-base md:text-lg font-bold leading-snug !text-[hsl(var(--fx-fg))]">{pooja.name}</h4>
          {pooja.startingPrice ? (
            <p className="font-sans text-xs text-[hsl(var(--fx-muted))]">
              From <span className="font-semibold text-[hsl(var(--fx-fg))]">{formatPrice(pooja.startingPrice)}</span>
            </p>
          ) : null}
          <span className="mt-auto inline-flex items-center gap-1 pt-1 text-sm font-semibold text-[hsl(var(--fx-accent))]">
            View packages <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
      {/* Sibling of the card link (a link inside a link is invalid HTML). */}
      {LEAD_CTAS.cardWhatsapp && campaign && (
        <a
          href={festivalWaLink({ campaign, slot: `${slotKey}.card`, pooja: pooja.name, city: pooja.linkLocation || location, intent: `I'd like help planning ${pooja.name} for ${campaign.festivalName} — muhurat, samagri and what's included` })}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLeadClick(campaign, "whatsapp", `${slotKey}.card`, pooja.slug)}
          aria-label={`Plan ${pooja.name} on WhatsApp`}
          className="absolute right-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-full bg-[#1fa855] py-1 pl-1.5 pr-2.5 font-sans text-[11px] font-bold text-white shadow-lg transition-transform hover:scale-105"
        >
          <LeadTag n={3} className="-left-3 -top-3" />
          <FaWhatsapp className="h-4 w-4" /> Plan This Puja
        </a>
      )}
    </div>
  );
};

export default FestivalPoojaCard;
